/**
 * Сеанс входа.
 *
 * Здесь живёт токен, выданный Core, и то, что Core о человеке
 * рассказал. Токен ездит в каждом запросе заголовком `Authorization` —
 * см. `api/client.ts`.
 *
 * **Токен лежит в хранилище браузера, и это осознанный размен.** Cookie
 * с флагом `httpOnly` была бы недоступна скриптам и потому безопаснее,
 * но терминал и Core живут на разных портах: браузер отправляет чужую
 * cookie только под `SameSite=None; Secure`, то есть только по HTTPS, —
 * в разработке такой вход не работал бы вовсе. Пока на страницу не
 * приходит чужой код, разница между хранилищем и cookie теоретическая;
 * когда придёт — эта заметка станет важной.
 *
 * Ключ хранилища с версией: если завтра форма записи изменится, старая
 * не притворится новой, а просто не подойдёт, и человек войдёт заново.
 */

import { useEffect, useState } from 'react';

const KEY = 'specter.session.v2';

/** Что о человеке знает терминал. Пароля здесь нет и быть не может. */
export interface Session {
  readonly token: string;
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  /** Привязан ли Telegram. */
  readonly telegram: boolean;
  /**
   * Аватарка `data:`-строкой, если Telegram её отдал.
   *
   * Пусто — рисуем монограмму из первой буквы имени. Отсутствие
   * фотографии в Telegram — обычное дело, а не поломка.
   */
  readonly avatar: string | null;
}

/*
 * Подписчики держатся здесь, а не в React-контексте.
 *
 * Сеанс читают и оболочка, и кабинет, и клиент API — последний вообще
 * не компонент. Контекст заставил бы обернуть провайдером всё
 * приложение ради значения, которое меняется дважды за сеанс.
 */
const listeners = new Set<(session: Session | null) => void>();

let current: Session | null = read();

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<Session>;
    if (typeof parsed?.token !== 'string' || parsed.token === '') return null;
    if (typeof parsed.id !== 'string' || parsed.id === '') return null;

    /*
     * Токен обязан годиться в заголовок HTTP.
     *
     * Найдено проверкой: заголовок принимает только печатные ASCII, и
     * `fetch` со значением вне этого набора бросает обычную ошибку — не
     * `401`. Испорченная запись тогда ломала бы каждый запрос подряд, а
     * человек оставался бы в интерфейсе, где ничего не грузится, вместо
     * того чтобы просто войти заново.
     */
    if (!/^[\t\x20-\x7e]+$/.test(parsed.token)) return null;

    return {
      token: parsed.token,
      id: parsed.id,
      name: typeof parsed.name === 'string' ? parsed.name : 'Трейдер',
      email: typeof parsed.email === 'string' ? parsed.email : null,
      telegram: parsed.telegram === true,
      /*
       * Аватарка принимается только `data:`-строкой.
       *
       * Записи в хранилище могут пережить обновление платформы, а
       * подставлять в `src` что попало из localStorage — это дать
       * чужому адресу нарисоваться от нашего имени.
       */
      avatar:
        typeof parsed.avatar === 'string' && parsed.avatar.startsWith('data:image/')
          ? parsed.avatar
          : null,
    };
  } catch {
    // Испорченная запись и её отсутствие — одно и то же: человек не
    // вошёл. Падать на старте из-за мусора в хранилище нельзя.
    return null;
  }
}

function write(session: Session | null): void {
  current = session;

  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session));
    else localStorage.removeItem(KEY);
  } catch {
    /*
     * Приватное окно и запрет на хранение — обычное дело. Сеанс тогда
     * живёт до перезагрузки страницы, и это лучше, чем отказать во
     * входе целиком: значение уже лежит в `current`.
     */
  }

  for (const listener of listeners) listener(session);
}

export function currentSession(): Session | null {
  return current;
}

/**
 * Что принесено из адреса после возвращения от Google.
 *
 * Сервер возвращает человека на `#вход=…` — в якоре, а не в запросе:
 * якорь браузер на сервер не отправляет, и токен не осядет ни в журнале
 * nginx, ни в заголовке `Referer` при первом же переходе.
 *
 * Читается один раз, при загрузке модуля, и адресная строка тут же
 * чистится: перезагрузка страницы не должна повторно втаскивать токен,
 * а показывать его человеку незачем.
 */
let broughtToken: string | null = null;
let broughtProblem: 'отказ' | 'отменён' | null = null;

function readReturn(): void {
  const where = globalThis.location;
  // В проверках и на сервере адреса нет — и приносить нечего.
  if (!where || typeof where.hash !== 'string') return;

  const match = /^#вход=(.+)$/.exec(where.hash);
  if (!match) return;

  const value = decodeURIComponent(match[1] ?? '');
  if (value === 'отказ' || value === 'отменён') broughtProblem = value;
  else if (value !== '') broughtToken = value;

  try {
    globalThis.history?.replaceState(null, '', where.pathname + where.search);
  } catch {
    // Старый браузер без истории — адрес останется, вход от этого не сломается.
  }
}

readReturn();

/** Токен, принесённый от Google, — пока сеанс ещё не собран. */
export function broughtFromGoogle(): string | null {
  return broughtToken;
}

/** Google отказал или человек передумал. Показывается один раз. */
export function googleProblem(): 'отказ' | 'отменён' | null {
  return broughtProblem;
}

/**
 * Токен для заголовка. `null` — не вошли.
 *
 * Принесённый от Google годится до того, как сеанс собран: им же
 * терминал и спрашивает «кто я», чтобы собрать остальное.
 */
export function authToken(): string | null {
  return current?.token ?? broughtToken;
}

/**
 * Обновить сведения о человеке, не трогая токен.
 *
 * Токен остаётся прежним: обновляется только то, что Core знает о
 * владельце, — имя, почта, аватарка. Нет сеанса — обновлять нечего.
 */
export function refreshUser(user: {
  id: string;
  name: string;
  email: string | null;
  telegram: boolean;
  avatar?: string | null;
}): void {
  if (!current) return;
  write({
    token: current.token,
    id: user.id,
    name: user.name,
    email: user.email,
    telegram: user.telegram,
    avatar: user.avatar ?? null,
  });
}

/** Запомнить выданный Core сеанс. */
export function saveSession(input: {
  token: string;
  user: {
    id: string;
    name: string;
    email: string | null;
    telegram: boolean;
    avatar?: string | null;
  };
}): void {
  // Принесённое из адреса больше не нужно: сеанс собран целиком.
  broughtToken = null;
  write({
    token: input.token,
    id: input.user.id,
    name: input.user.name,
    email: input.user.email,
    telegram: input.user.telegram,
    avatar: input.user.avatar ?? null,
  });
}

/**
 * Забыть сеанс.
 *
 * Зовётся и при выходе по кнопке, и когда Core ответил «не пущу»: токен
 * мог истечь или быть закрыт с другого устройства, и держаться за него
 * дальше незачем.
 */
export function forgetSession(): void {
  if (current !== null) write(null);
}

/** Сеанс как состояние React: перерисовка на вход и на выход. */
export function useSession(): Session | null {
  const [session, setSession] = useState<Session | null>(current);

  useEffect(() => {
    listeners.add(setSession);
    // Между первой отрисовкой и подпиской вход мог уже случиться.
    setSession(current);
    return () => {
      listeners.delete(setSession);
    };
  }, []);

  return session;
}
